import { useState, useRef, useEffect } from 'react';
import { PopulationRow } from '../types/insights';
import { EditableSelect, type SelectOption } from '@/components/ui/editable-select';
import { EditableInput } from '@/components/ui/editable-input';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { usePopulation } from '../hooks/usePopulation';
import { PROMOTION_YEAR_WINDOW, EARLIEST_PROMOTION_AGGREGATE_YEAR } from '@promo/shared/src/config/constants';

const currentYear = new Date().getFullYear();
const firstYear = EARLIEST_PROMOTION_AGGREGATE_YEAR;
const yearOptions: SelectOption[] = Array.from({ length: PROMOTION_YEAR_WINDOW }, (_, i) => {
  const y = (firstYear + i).toString();
  return { value: y, label: y };
});

type FocusField = 'year' | 'promote' | 'defer' | 'population' | null;

export function PopulationTable() {
  // Function options are static for demo; in real app, fetch from API or context
  const functionOptions: SelectOption[] = [
    { value: 'engineering', label: 'Engineering' },
    { value: 'product', label: 'Product' },
    { value: 'design', label: 'Design' },
    { value: 'techops', label: 'TechOps' },
  ];
  const [selectedFunction, setSelectedFunction] = useState(functionOptions[0].value);

  // API integration
  const {
    data: rows,
    isLoading,
    error,
    addOrUpdatePopulation,
    deletePopulation,
    isSaving,
  } = usePopulation(selectedFunction);

  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<PopulationRow> | null>(null);
  const [showDeleteIdx, setShowDeleteIdx] = useState<number | null>(null);
  const [focusField, setFocusField] = useState<FocusField>(null);
  const [validationErrors, setValidationErrors] = useState<{ [key in Exclude<FocusField, null>]?: string }>({});
  const yearRef = useRef<HTMLButtonElement | null>(null);
  const promoteRef = useRef<HTMLInputElement | null>(null);
  const deferRef = useRef<HTMLInputElement | null>(null);
  const populationRef = useRef<HTMLInputElement | null>(null);

  const filteredRows = rows.filter(row => row.function === selectedFunction);

  // Find the largest year for the selected function
  const largestYear = filteredRows.reduce((max, row) => {
    const y = parseInt(row.year, 10);
    return y > max ? y : max;
  }, 0);

  // Should we show a blank row for the current year?
  const shouldShowBlankRow = largestYear < currentYear;

  // Validation logic
  useEffect(() => {
    const filteredRows = rows.filter(row => row.function === selectedFunction);
    if (!editDraft) {
      setValidationErrors({});
      return;
    }
    const errors: { [key in Exclude<FocusField, null>]?: string } = {};
    const { promote, defer, population, year } = editDraft;
    if (typeof promote === 'number' && promote < 0) {
      errors['promote'] = 'Promote must be 0 or greater';
    }
    if (typeof defer === 'number' && defer < 0) {
      errors['defer'] = 'Defer must be 0 or greater';
    }
    if (population === undefined || population === null || population < 0) {
      errors['population'] = 'Population must be 0 or greater';
    }
    if (
      typeof promote === 'number' && typeof defer === 'number' && typeof population === 'number' &&
      population < (promote + defer)
    ) {
      errors['population'] = `Population must be at least promote + defer (${promote + defer})`;
    }
    // Unique year validation
    if (year) {
      let globalIdx = -1;
      if (editingIdx !== null) {
        const editingRow = filteredRows[editingIdx];
        globalIdx = rows.findIndex(r => r === editingRow);
      }
      const duplicate = rows.some((r, idx) =>
        r.function === selectedFunction &&
        r.year === year &&
        idx !== globalIdx
      );
      if (duplicate) {
        errors['year'] = 'Year must be unique for this function';
      }
    }
    setValidationErrors(errors);
  }, [editDraft, rows, editingIdx, selectedFunction]);

  useEffect(() => {
    if (editingIdx !== null && focusField) {
      if (focusField === 'year' && yearRef.current) {
        yearRef.current.focus();
        if (typeof yearRef.current.click === 'function') {
          yearRef.current.click();
        }
      }
      if (focusField === 'promote' && promoteRef.current) promoteRef.current.focus();
      if (focusField === 'defer' && deferRef.current) deferRef.current.focus();
      if (focusField === 'population' && populationRef.current) populationRef.current.focus();
      setFocusField(null);
    }
  }, [editingIdx, focusField]);

  const startEdit = (idx: number, field: FocusField) => {
    setEditingIdx(idx);
    setEditDraft({ ...filteredRows[idx] });
    setFocusField(field);
  };

  const cancelEdit = () => {
    setEditingIdx(null);
    setEditDraft(null);
    setFocusField(null);
  };

  const saveEdit = () => {
    if (editingIdx === null || !editDraft || Object.keys(validationErrors).length > 0) return;
    addOrUpdatePopulation({
      year: editDraft.year as string,
      function: selectedFunction,
      promote: editDraft.promote,
      defer: editDraft.defer,
      population: editDraft.population !== undefined ? editDraft.population : 0,
      source: filteredRows[editingIdx]?.source || 'current',
      lastUpdated: new Date().toISOString().slice(0, 10),
      lastUpdatedBy: 'admin',
    });
    setEditingIdx(null);
    setEditDraft(null);
    setFocusField(null);
  };

  const deleteRow = (idx: number) => {
    const row = filteredRows[idx];
    if (row) {
      deletePopulation(row);
    }
    setShowDeleteIdx(null);
    if (editingIdx === idx) {
      setEditingIdx(null);
      setEditDraft(null);
      setFocusField(null);
    }
  };

  // Handler for saving a new blank row
  const saveNewRow = () => {
    if (!editDraft || Object.keys(validationErrors).length > 0) return;
    addOrUpdatePopulation({
      year: editDraft.year as string,
      function: selectedFunction,
      promote: editDraft.promote,
      defer: editDraft.defer,
      population: editDraft.population !== undefined ? editDraft.population : 0,
      source: 'current',
      lastUpdated: new Date().toISOString().slice(0, 10),
      lastUpdatedBy: 'admin',
    });
    setEditingIdx(null);
    setEditDraft(null);
    setFocusField(null);
  };

  // Loading and error states
  if (isLoading) {
    return <div className="p-8 text-center text-muted-foreground">Loading population data...</div>;
  }
  if (error) {
    return <div className="p-8 text-center text-destructive">Failed to load population data: {error.message}</div>;
  }

  return (
    <div>
      <div className="mb-4">
        <EditableSelect
          id="function-filter"
          options={functionOptions}
          value={selectedFunction}
          onValueChange={setSelectedFunction}
          placeholder="Select function"
          className="w-[180px]"
        />
      </div>
      <div className="space-y-4">
        {filteredRows.length === 0 ? (
          <div className="text-muted-foreground p-4">No data for selected function.</div>
        ) : (
          filteredRows.map((row, i) => {
            const isEditing = editingIdx === i;
            return (
              <div
                key={i}
                className={["p-4 grid grid-cols-2 md:grid-cols-2 gap-x-6 gap-y-4 cursor-pointer relative", i % 2 === 1 ? "bg-gray-100" : ""].join(" ")}
                // Remove row-level onClick
              >
                {/* Year */}
                <div className="relative">
                  <div className="text-xs uppercase text-muted-foreground mb-1">Year</div>
                  {isEditing ? (
                    <>
                      <EditableSelect
                        ref={yearRef}
                        options={yearOptions}
                        value={editDraft?.year || ''}
                        onValueChange={val => setEditDraft(d => ({ ...d, year: val }))}
                        placeholder="Year"
                        className="w-[100px]"
                      />
                      {validationErrors.year && (
                        <div className="text-xs text-destructive mt-1">{validationErrors.year}</div>
                      )}
                    </>
                  ) : (
                    <div className="text-base min-h-[40px] py-2 flex items-center relative">
                      {row.year}
                      <div
                        className="absolute inset-0 z-10 cursor-pointer"
                        onClick={() => startEdit(i, 'year')}
                        aria-label="Edit year"
                      />
                    </div>
                  )}
                </div>
                {/* Promote */}
                <div className="relative">
                  <div className="text-xs uppercase text-muted-foreground mb-1">Promote</div>
                  {isEditing ? (
                    <>
                      <EditableInput
                        ref={promoteRef}
                        type="number"
                        value={editDraft?.promote ?? ''}
                        onChange={e => setEditDraft(d => ({ ...d, promote: e.target.value === '' ? undefined : Number(e.target.value) }))}
                        placeholder="Promote"
                        min={0}
                        className={validationErrors.promote ? 'border-destructive text-destructive' : ''}
                      />
                      {validationErrors.promote && (
                        <div className="text-xs text-destructive mt-1">{validationErrors.promote}</div>
                      )}
                    </>
                  ) : (
                    <div className="text-base min-h-[40px] py-2 flex items-center relative">
                      {row.promote ?? '-'}
                      <div
                        className="absolute inset-0 z-10 cursor-pointer"
                        onClick={() => startEdit(i, 'promote')}
                        aria-label="Edit promote"
                      />
                    </div>
                  )}
                </div>
                {/* Defer */}
                <div className="relative">
                  <div className="text-xs uppercase text-muted-foreground mb-1">Defer</div>
                  {isEditing ? (
                    <>
                      <EditableInput
                        ref={deferRef}
                        type="number"
                        value={editDraft?.defer ?? ''}
                        onChange={e => setEditDraft(d => ({ ...d, defer: e.target.value === '' ? undefined : Number(e.target.value) }))}
                        placeholder="Defer"
                        min={0}
                        className={validationErrors.defer ? 'border-destructive text-destructive' : ''}
                      />
                      {validationErrors.defer && (
                        <div className="text-xs text-destructive mt-1">{validationErrors.defer}</div>
                      )}
                    </>
                  ) : (
                    <div className="text-base min-h-[40px] py-2 flex items-center relative">
                      {row.defer ?? '-'}
                      <div
                        className="absolute inset-0 z-10 cursor-pointer"
                        onClick={() => startEdit(i, 'defer')}
                        aria-label="Edit defer"
                      />
                    </div>
                  )}
                </div>
                {/* Population */}
                <div className="relative">
                  <div className="text-xs uppercase text-muted-foreground mb-1">Population</div>
                  {isEditing ? (
                    <>
                      <EditableInput
                        ref={populationRef}
                        type="number"
                        value={editDraft?.population ?? ''}
                        onChange={e => setEditDraft(d => ({ ...d, population: e.target.value === '' ? undefined : Number(e.target.value) }))}
                        placeholder="Population"
                        min={0}
                        className={validationErrors.population ? 'border-destructive text-destructive' : ''}
                      />
                      {validationErrors.population && (
                        <div className="text-xs text-destructive mt-1">{validationErrors.population}</div>
                      )}
                    </>
                  ) : (
                    <div className="text-base min-h-[40px] py-2 flex items-center relative">
                      {row.population}
                      <div
                        className="absolute inset-0 z-10 cursor-pointer"
                        onClick={() => startEdit(i, 'population')}
                        aria-label="Edit population"
                      />
                    </div>
                  )}
                </div>
                {/* Actions (edit mode only) */}
                {isEditing && (
                  <div className="col-span-2 flex justify-end gap-2 mt-4">
                    <Button
                      type="button"
                      variant="ghost"
                      aria-label="Delete row"
                      onClick={e => { e.stopPropagation(); setShowDeleteIdx(i); }}
                      className="mr-auto"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="outline" onClick={e => { e.stopPropagation(); cancelEdit(); }}>Cancel</Button>
                    <Button type="button" onClick={e => { e.stopPropagation(); saveEdit(); }} disabled={Object.keys(validationErrors).length > 0}>Save</Button>
                    {/* Delete confirmation dialog */}
                    <AlertDialog open={showDeleteIdx === i} onOpenChange={open => { if (!open) setShowDeleteIdx(null); }}>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete row</AlertDialogTitle>
                          <AlertDialogDescription>Are you sure? This action cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel onClick={() => setShowDeleteIdx(null)}>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteRow(i)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
            );
          })
        )}
        {/* Blank row for current year if needed */}
        {shouldShowBlankRow && editingIdx === null && (
          <div className="p-4 grid grid-cols-2 md:grid-cols-2 gap-x-6 gap-y-4 cursor-pointer relative bg-gray-50" onClick={() => {
            setEditDraft({ year: String(currentYear), function: selectedFunction });
            setEditingIdx(filteredRows.length); // new row index
            setFocusField('year');
          }}>
            <div className="relative">
              <div className="text-xs uppercase text-muted-foreground mb-1">Year</div>
              <div className="text-base min-h-[40px] py-2 flex items-center relative text-muted-foreground">{currentYear}</div>
            </div>
            <div className="relative">
              <div className="text-xs uppercase text-muted-foreground mb-1">Promote</div>
              <div className="text-base min-h-[40px] py-2 flex items-center relative text-muted-foreground">-</div>
            </div>
            <div className="relative">
              <div className="text-xs uppercase text-muted-foreground mb-1">Defer</div>
              <div className="text-base min-h-[40px] py-2 flex items-center relative text-muted-foreground">-</div>
            </div>
            <div className="relative">
              <div className="text-xs uppercase text-muted-foreground mb-1">Population</div>
              <div className="text-base min-h-[40px] py-2 flex items-center relative text-muted-foreground">-</div>
            </div>
          </div>
        )}
        {/* Edit mode for new blank row */}
        {shouldShowBlankRow && editingIdx === filteredRows.length && (
          <div className="p-4 grid grid-cols-2 md:grid-cols-2 gap-x-6 gap-y-4 relative bg-gray-50">
            {/* Year */}
            <div className="relative">
              <div className="text-xs uppercase text-muted-foreground mb-1">Year</div>
              <>
                <EditableSelect
                  ref={yearRef}
                  options={yearOptions}
                  value={editDraft?.year || ''}
                  onValueChange={val => setEditDraft(d => ({ ...d, year: val }))}
                  placeholder="Year"
                  className="w-[100px]"
                />
                {validationErrors.year && (
                  <div className="text-xs text-destructive mt-1">{validationErrors.year}</div>
                )}
              </>
            </div>
            {/* Promote */}
            <div className="relative">
              <div className="text-xs uppercase text-muted-foreground mb-1">Promote</div>
              <>
                <EditableInput
                  ref={promoteRef}
                  type="number"
                  value={editDraft?.promote ?? ''}
                  onChange={e => setEditDraft(d => ({ ...d, promote: e.target.value === '' ? undefined : Number(e.target.value) }))}
                  placeholder="Promote"
                  min={0}
                  className={validationErrors.promote ? 'border-destructive text-destructive' : ''}
                />
                {validationErrors.promote && (
                  <div className="text-xs text-destructive mt-1">{validationErrors.promote}</div>
                )}
              </>
            </div>
            {/* Defer */}
            <div className="relative">
              <div className="text-xs uppercase text-muted-foreground mb-1">Defer</div>
              <>
                <EditableInput
                  ref={deferRef}
                  type="number"
                  value={editDraft?.defer ?? ''}
                  onChange={e => setEditDraft(d => ({ ...d, defer: e.target.value === '' ? undefined : Number(e.target.value) }))}
                  placeholder="Defer"
                  min={0}
                  className={validationErrors.defer ? 'border-destructive text-destructive' : ''}
                />
                {validationErrors.defer && (
                  <div className="text-xs text-destructive mt-1">{validationErrors.defer}</div>
                )}
              </>
            </div>
            {/* Population */}
            <div className="relative">
              <div className="text-xs uppercase text-muted-foreground mb-1">Population</div>
              <>
                <EditableInput
                  ref={populationRef}
                  type="number"
                  value={editDraft?.population ?? ''}
                  onChange={e => setEditDraft(d => ({ ...d, population: e.target.value === '' ? undefined : Number(e.target.value) }))}
                  placeholder="Population"
                  min={0}
                  className={validationErrors.population ? 'border-destructive text-destructive' : ''}
                />
                {validationErrors.population && (
                  <div className="text-xs text-destructive mt-1">{validationErrors.population}</div>
                )}
              </>
            </div>
            {/* Actions */}
            <div className="col-span-2 flex justify-end gap-2 mt-4">
              <Button type="button" variant="outline" onClick={() => { setEditingIdx(null); setEditDraft(null); setFocusField(null); }}>Cancel</Button>
              <Button type="button" onClick={saveNewRow} disabled={Object.keys(validationErrors).length > 0 || isSaving}>Save</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 